<script setup>
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import AgentApprovalQueue from 'dashboard/routes/dashboard/captain/assistants/experience/components/AgentApprovalQueue.vue';

const props = defineProps({
  showApprovals: { type: Boolean, default: false },
  canManage: { type: Boolean, default: false },
  approvals: { type: Array, default: () => [] },
  activeApprovalId: { type: [Number, String], default: null },
  approvalsMeta: { type: Object, default: () => ({}) },
  hasMoreApprovals: { type: Boolean, default: false },
  isLoadingMoreApprovals: { type: Boolean, default: false },
  governance: { type: Object, required: true },
  isSavingGovernance: { type: Boolean, default: false },
});

const emit = defineEmits([
  'decide',
  'loadMore',
  'update:governance',
  'saveGovernance',
]);

const updateGovernance = (field, value) => {
  emit('update:governance', { ...props.governance, [field]: value });
};
const handleDecision = (...args) => emit('decide', ...args);
</script>

<template>
  <section v-if="showApprovals && canManage" class="flex flex-col gap-4">
    <SettingsHeader
      :heading="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.APPROVALS_TITLE')"
      :description="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.APPROVALS_DESCRIPTION')"
    />
    <AgentApprovalQueue
      :approvals="approvals"
      :active-approval-id="activeApprovalId"
      :can-decide="canManage"
      :title="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.APPROVALS_QUEUE')"
      :empty-label="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.APPROVALS_EMPTY')"
      :approve-label="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.APPROVE')"
      :reject-label="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.REJECT')"
      @decide="handleDecision"
    />
    <p class="text-xs text-n-slate-10">
      {{
        $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.APPROVALS_COUNT', {
          count: approvalsMeta.total_count,
        })
      }}
    </p>
    <Button
      v-if="hasMoreApprovals"
      type="button"
      class="self-start"
      :label="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.LOAD_MORE_APPROVALS')"
      :is-loading="isLoadingMoreApprovals"
      slate
      sm
      faded
      @click="emit('loadMore')"
    />
  </section>

  <section class="flex flex-col gap-4">
    <SettingsHeader
      :heading="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.GOVERNANCE_TITLE')"
      :description="
        $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.GOVERNANCE_DESCRIPTION')
      "
    />
    <div class="flex flex-col divide-y divide-n-weak">
      <label class="flex items-start gap-3 py-4 first:pt-0">
        <Checkbox
          :model-value="governance.featureFaq"
          :disabled="!canManage"
          @update:model-value="updateGovernance('featureFaq', $event)"
        />
        <span>
          <span class="block text-sm font-medium text-n-slate-12">
            {{ $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.FEATURE_FAQ') }}
          </span>
          <span class="mt-1 block text-xs text-n-slate-11">
            {{ $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.FEATURE_FAQ_HELP') }}
          </span>
        </span>
      </label>
      <label class="flex items-start gap-3 py-4">
        <Checkbox
          :model-value="governance.featureMemory"
          :disabled="!canManage"
          @update:model-value="updateGovernance('featureMemory', $event)"
        />
        <span>
          <span class="block text-sm font-medium text-n-slate-12">
            {{ $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.FEATURE_MEMORY') }}
          </span>
          <span class="mt-1 block text-xs text-n-slate-11">
            {{ $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.FEATURE_MEMORY_HELP') }}
          </span>
        </span>
      </label>
      <label class="flex items-start gap-3 py-4">
        <Checkbox
          :model-value="governance.disableDatabaseTools"
          :disabled="!canManage"
          @update:model-value="updateGovernance('disableDatabaseTools', $event)"
        />
        <span>
          <span class="block text-sm font-medium text-n-slate-12">
            {{ $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.DISABLE_DATABASE_TOOLS') }}
          </span>
          <span class="mt-1 block text-xs text-n-slate-11">
            {{
              $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.DISABLE_DATABASE_TOOLS_HELP')
            }}
          </span>
        </span>
      </label>
      <label class="flex items-start gap-3 py-4">
        <Checkbox
          :model-value="governance.allowSensitiveData"
          :disabled="!canManage"
          @update:model-value="updateGovernance('allowSensitiveData', $event)"
        />
        <span>
          <span
            class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
          >
            <span
              class="i-lucide-triangle-alert size-4 text-n-ruby-11"
              aria-hidden="true"
            />
            {{ $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.ALLOW_SENSITIVE_DATA') }}
          </span>
          <span class="mt-1 block text-xs leading-relaxed text-n-slate-11">
            {{
              $t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.ALLOW_SENSITIVE_DATA_HELP')
            }}
          </span>
        </span>
      </label>
    </div>
    <Button
      v-if="canManage"
      type="button"
      :label="$t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.SAVE_GOVERNANCE')"
      icon="i-lucide-save"
      :is-loading="isSavingGovernance"
      class="w-fit"
      @click="emit('saveGovernance')"
    />
  </section>
</template>
